import React, { useCallback, useRef, useState, useEffect } from 'react';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import closeIcon from '../assets/icons/close-icon.svg';
import usePopupStore from '../store/popupStore';

const Popup = () => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const { popupContent, popupTitle, popupSize, setPopup } = usePopupStore();

  const [animationClass, setAnimationClass] = useState(
    'animate__fadeInDownBig'
  );

  const getScreenDimensions = () => ({
    vw: typeof window !== 'undefined' ? window.innerWidth : 0,
    vh: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  const [screenDimensions, setScreenDimensions] = useState(getScreenDimensions);

  useEffect(() => {
    const handleResize = () => setScreenDimensions(getScreenDimensions());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { vw, vh } = screenDimensions;
  const isMobile = vw < 640;

  // Presets for different sizes — on mobile always use full viewport width
  const presets = {
    sm: {
      width: isMobile ? vw : Math.min(Math.max(vw * 0.4, 400), 600),
      height: isMobile
        ? Math.min(vh * 0.85, vh - 20)
        : Math.min(Math.max(vh * 0.4, 250), 400),
      min: [isMobile ? vw : 300, 200] as [number, number],
      max: [isMobile ? vw : 700, isMobile ? vh : 500] as [number, number],
    },
    md: {
      width: isMobile ? vw : Math.min(Math.max(vw * 0.8, 500), 900),
      height: isMobile
        ? Math.min(vh * 0.85, vh - 20)
        : Math.min(Math.max(vh * 0.8, 400), 700),
      min: [isMobile ? vw : 500, isMobile ? 200 : 400] as [number, number],
      max: [isMobile ? vw : 900, isMobile ? vh : 700] as [number, number],
    },
    lg: {
      width: isMobile ? vw : Math.min(Math.max(vw * 0.9, 700), 1100),
      height: isMobile
        ? Math.min(vh * 0.9, vh - 20)
        : Math.min(Math.max(vh * 0.9, 500), 900),
      min: [isMobile ? vw : 700, isMobile ? 200 : 500] as [number, number],
      max: [isMobile ? vw : 1200, isMobile ? vh : 900] as [number, number],
    },
  } as const;

  const { width, height, min, max } = presets[popupSize ?? 'md'];

  const handleClose = useCallback(() => {
    setAnimationClass('animate__fadeOutUpBig');
    setTimeout(() => {
      setPopup('', null);
    }, 300);
  }, [setPopup]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (nodeRef.current && !nodeRef.current.contains(event.target as Node)) {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div className="fixed backdrop-blur-sm text-xs w-full h-dvh z-[100] items-center justify-center flex bg-black/40 left-0 right-0 top-0 bottom-0 py-[var(--safe-top)] px-[var(--safe-left)]">
      <Draggable handle="#title" nodeRef={nodeRef} disabled={isMobile}>
        <div ref={nodeRef}>
          <ResizableBox
            width={width}
            height={height}
            minConstraints={min}
            maxConstraints={max}
            resizeHandles={isMobile ? [] : ['sw', 'se', 'nw']}
            className={`bg-white animate__animated ${animationClass} animate__fast shadow rounded-md`}
          >
            <div className="w-full h-full flex flex-col">
              <div
                id="title"
                style={{ boxShadow: '0 10px 5px 0 rgba(0, 0, 0, 0.05)' }}
                className="p-3 flex items-center bg-gray-50 justify-between border-b text-xs= rounded-t-md text-sm font-medium cursor-move"
              >
                {popupTitle}
                <img
                  className="cursor-pointer"
                  width={18}
                  onClick={handleClose}
                  src={closeIcon}
                  alt=""
                />
              </div>
              <div className="flex-1 bg-gray-100 overflow-x-scroll">
                {popupContent}
              </div>
            </div>
          </ResizableBox>
        </div>
      </Draggable>
    </div>
  );
};

export default Popup;
